import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import styles from './PunchupSendTicketsFormSeated.module.css';
import { PunchupPersonEntry } from 'features/common/components/Punchup/composites/forms/PunchupPersonEntry';
import { PunchupInputToggle } from 'features/common/components/Punchup/primitives/forms/fields/PunchupInputToggle';
import { PunchupSeatsCategoryBreakdown } from 'features/common/components/Punchup/primitives/display/PunchupSeatsCategoryBreakdown';
import { PunchupBuyerSeatsWidget } from 'features/common/components/Punchup/composites/seats/PunchupBuyerSeatsWidget';
import { useSeatsSelection } from 'features/common/hooks/useSeatsSelection';
import useGetPartnerData from 'features/common/hooks/useGetPartnerData';
import { useTranslation } from 'react-i18next';

import { RecipientTicketType, SendTicketsEventProps } from '../../types';

interface PunchupSendTicketsFormSeatedProps {
	event: SendTicketsEventProps;
	// Incremented by the page to clear chart selection state inside useSeatsSelection,
	// which lives outside react-hook-form. Zero skips the reset on initial render.
	resetSignal?: number;
}

export const PunchupSendTicketsFormSeated = ({ event, resetSignal }: PunchupSendTicketsFormSeatedProps) => {
	const { t } = useTranslation();
	const { partnerData } = useGetPartnerData();
	const { setValue, watch } = useFormContext();
	const noEmail = !!watch('recipients[0].noEmail');

	const {
		selectionByCategory,
		selection,
		chartProps,
		reset: resetSelection,
	} = useSeatsSelection(event.seatsEventKey, 'select');

	const categoryToTicketType = useMemo(() => {
		const map = new Map<string, RecipientTicketType>();
		event.ticketTypes.forEach((tt) => {
			if (tt.seatsCategoryKey) map.set(String(tt.seatsCategoryKey), tt);
		});
		return map;
	}, [event.ticketTypes]);

	const totalSelected = selection.length;

	const hasUnboundSeat = useMemo(
		() => selectionByCategory.some((g) => g.objectLabels.length > 0 && !categoryToTicketType.get(g.categoryKey)),
		[selectionByCategory, categoryToTicketType]
	);

	const submitGateState: 'ok' | 'no-secret' | 'empty-selection' | 'unbound' = useMemo(() => {
		if (!partnerData?.seats_io_workspace_key) return 'no-secret';
		if (totalSelected === 0) return 'empty-selection';
		if (hasUnboundSeat) return 'unbound';
		return 'ok';
	}, [partnerData?.seats_io_workspace_key, totalSelected, hasUnboundSeat]);

	const recipientTicketTypes = useMemo<RecipientTicketType[]>(() => {
		const byTicketTypeId = new Map<number, RecipientTicketType & { seats: string[] }>();
		selectionByCategory.forEach((g) => {
			const tt = categoryToTicketType.get(g.categoryKey);
			if (!tt) return;
			const existing = byTicketTypeId.get(tt.ticketTypeId);
			if (existing) {
				existing.quantity += g.objectLabels.length;
				existing.seats.push(...g.objectLabels);
			} else {
				byTicketTypeId.set(tt.ticketTypeId, {
					type: tt.type,
					quantity: g.objectLabels.length,
					initialPrice: tt.initialPrice,
					ticket_type_id: tt.ticketTypeId,
					ticketTypeId: tt.ticketTypeId,
					amount: tt.amount,
					soldOut: tt.soldOut,
					sold: tt.sold,
					seatsCategoryKey: tt.seatsCategoryKey,
					seats: [...g.objectLabels],
				});
			}
		});
		return Array.from(byTicketTypeId.values());
	}, [selectionByCategory, categoryToTicketType]);

	useEffect(() => {
		if (submitGateState !== 'ok') {
			setValue('recipients[0].ticketTypes', []);
			return;
		}
		setValue('recipients[0].ticketTypes', recipientTicketTypes);
	}, [submitGateState, recipientTicketTypes, setValue]);

	useEffect(() => {
		if (resetSignal === undefined || resetSignal === 0) return;
		resetSelection();
	}, [resetSignal, resetSelection]);

	const displayGroups = selectionByCategory;
	const displayTotal = totalSelected;
	const displayGateState = submitGateState;

	return (
		<div className={styles.root}>
			<PunchupPersonEntry
				emailName="recipients[0].email"
				firstNameName="recipients[0].firstName"
				lastNameName="recipients[0].lastName"
				hideEmail={noEmail}
				belowEmail={
					<PunchupInputToggle
						htmlFor="recipients[0].noEmail"
						name="recipients[0].noEmail"
						label={t('send-tickets-no-email', 'Recipient has no email')}
					/>
				}
			/>

			<div className={styles.summaryBlock} data-testid="seated-summary">
				<div className={styles.summaryHeader}>
					<span className={styles.summaryLabel}>{t('send-tickets-seated-selected-label', 'Selected seats')}</span>
					<span className={styles.summaryCount} data-testid="seated-summary-count">
						{t('send-tickets-seated-total-count', '{{count}} seats', { count: displayTotal })}
					</span>
				</div>
				{displayGateState === 'no-secret' && (
					<span className={styles.summaryError}>
						{t(
							'send-tickets-seated-missing-secret',
							'Seats.io credentials are not configured for this organisation. Contact support.'
						)}
					</span>
				)}
				{displayGateState === 'empty-selection' && (
					<span className={styles.summaryHint}>
						{t('send-tickets-seated-empty-selection', 'Click seats on the chart below to assign them to this guest.')}
					</span>
				)}
				{displayGateState === 'unbound' && (
					<span className={styles.summaryError}>
						{t(
							'send-tickets-seated-unbound',
							'A selected seat is in a category that is not bound to a ticket type. Bind it first in the seats.io chart.'
						)}
					</span>
				)}
				<PunchupSeatsCategoryBreakdown groups={displayGroups} />
			</div>

			<PunchupBuyerSeatsWidget eventKey={event.seatsEventKey} chartProps={chartProps} />
		</div>
	);
};
